import {createSelector} from 'reselect';
import get from 'lodash/get';

import getListingPageModalStates from './modalStates';
import {getSystem, getTranslations, getTripData} from './store';
import {getIsMobile, getLocaleJS, getSiteName} from './system';
import {getTravelerSegmentFeatures} from './travelerSegmentFeatures';
import {getPreferredLocation, getSpu} from './listing';
import {getIsTypeaheadInputFocused} from './listingPageState';
import {getCookiedGeoString} from '../utils/travelerSegments';

const getLabels = createSelector(
    [getTranslations, getTravelerSegmentFeatures],
    (translations, travelerSegmentFeatures) => {
        return {
            searchMoreProperties: translations.searchMoreProperties,
            mobileTitle: translations.search,
            buttonTitle: travelerSegmentFeatures.useCheckAvailabilityButton
                ? translations.checkAvailability
                : translations.search,
            datePicker: {
                label: translations.checkIn,
                label2: translations.checkOut,
                clear: translations.clearDates,
                close: translations.closeDatePicker,
                done: translations.done
            },
            guests: {
                label: translations.searchGuestsLabel,
                adult: translations.adultSingular,
                adults: translations.adultPlural,
                child: translations.childSingular,
                children: translations.childPlural,
                pets: translations.pets,
                guests: translations.guests,
                yesBtn: translations.guestSelectorYes,
                noBtn: translations.guestSelectorNo,
                clearBtn: translations.clear,
                applyBtn: translations.apply,
                modalTitle: translations.selectGuests,
                mobileBtn: translations.continue
            },
            skipToSearch: translations.skipToSearch,
            skipToSearchAnchour: translations.skipToSearchAnchour
        };
    }
);

const getLocation = createSelector(
    [getSystem, getTravelerSegmentFeatures, getPreferredLocation],
    (system, travelerSegmentFeatures, preferredLocation) => {
        if (travelerSegmentFeatures.showLastSearchedLocation) {
            return getCookiedGeoString(system.cookies);
        }
        const preferredLocationName = get(preferredLocation, 'name.full');
        if (preferredLocationName) {
            return preferredLocationName;
        }

        return null;
    }
);

const getGuests = createSelector(
    [getTripData],
    (tripData) => {
        return {
            adultsCount: tripData.adults,
            childrenCount: tripData.children,
            petIncluded: tripData.hasPets,
            adultsMin: 1
        };
    }
);

const getDates = createSelector(
    [getTripData],
    (tripData) => {
        return {
            arrive: tripData.arriveDateText,
            depart: tripData.departDateText
        };
    }
);

const getHideDates = createSelector(
    [],
    () => false
);

const getHideGuests = createSelector(
    [getIsMobile, getTravelerSegmentFeatures],
    (isMobile, travelerSegmentFeatures) =>
        !isMobile || travelerSegmentFeatures.hideSearchBoxGuestsMobile
);

const getSearchBoxSelector = createSelector(
    [
        getLabels,
        getListingPageModalStates,
        getLocation,
        getIsMobile,
        getLocaleJS,
        getSiteName,
        getTravelerSegmentFeatures,
        getGuests,
        getHideGuests,
        getDates,
        getHideDates,
        getIsTypeaheadInputFocused,
        getSpu
    ],
    (
        labels,
        modalStates,
        location,
        isMobile,
        localeJS,
        systemSiteName,
        travelerSegmentFeatures,
        guests,
        hideGuests,
        dates,
        hideDates,
        isTypeaheadInputFocused,
        spu
    ) => {
        return {
            isMobile,
            labels,
            location,
            localeJS,
            siteName: systemSiteName.toLowerCase(),
            guests,
            hideGuests,
            checkIn: dates.arrive,
            checkOut: dates.depart,
            hideDates,
            spu,
            isSearchModalOpen: modalStates.searchModalOpen,
            isSearchDatePickerOpen: modalStates.searchDatePickerOpen,
            isSearchGuestPickerOpen: modalStates.searchGuestPickerOpen,
            isTypeaheadInputFocused,
            useCheckAvailabilityButton: travelerSegmentFeatures.useCheckAvailabilityButton,
            usePrimarySearchBoxSubmit: travelerSegmentFeatures.usePrimarySearchBoxSubmit
        };
    }
);

export default getSearchBoxSelector;
export {
    getLabels,
    getLocation,
    getSearchBoxSelector,
    getGuests,
    getDates,
    getHideDates,
    getHideGuests
};
